<script setup lang="ts">
    import KForm from "@/components/forms/Form/KForm.vue";
    import { flashWarning } from "@/utils";
    import { AxiosError } from "axios";
    import cloneDeep from "lodash/cloneDeep";
    import { computed, onBeforeMount, onBeforeUnmount, ref, toRefs, watch } from "vue";
    import { useStore } from "@/store";
    import emitter from "tiny-emitter/instance";
    import MaterialRepeater from "@/tabs/Admin/forms/MaterialRepeater.vue";
    import { addMinimumRows } from "@/components/forms/groups/Repeater/helpers";
    import { convertMaterialData } from "@/tabs/Admin/forms/materialConverter";

    // TODO Ändra till användning av type i materialgruppModule i Vue3
    interface Materialgrupp {
        id: string;
        gruppnummer: string;
        namn: string;
        streckkod: string;
        material: {
            antal: number;
            material: string;
            materialmaterialgrupp?: number;
        }[];
    }

    // TODO Ändra till användning av type i materialgruppModule i Vue3
    interface Material {
        id: string;
        artikelnummer: string;
        namn: string;
        leverantor: string;
        streckkod1: string;
        streckkod2: string;
    }

    const store = useStore();

    const props = defineProps<{
        tabId: number; // TODO Ändra i Vue3
        parameters?: {
            materialgruppId: string;
        };
    }>();

    const { parameters, tabId } = toRefs(props);
    const errors = ref<{
        [key: string]: string[];
    }>({});
    const formRef = ref();
    const isEditing = computed(() => !!materialgrupp.value);
    const heading = computed(() => (isEditing.value ? "Redigera materialgrupp" : "Skapa materialgrupp"));
    const submitLabel = computed(() => (isEditing.value ? "Redigera materialgrupp" : "Skapa materialgrupp"));
    const materialgrupp = computed(() =>
        store.getters["materialgrupp/materialgruppById"]({
            id: parameters?.value?.materialgruppId,
        })
    );
    const hasUnsavedData = computed(() => formRef.value?.hasUnsavedData());

    defineExpose({
        hasUnsavedData,
        tabId,
    });

    // TODO convert to useTabs in v3
    const closeTab = ({
        override,
        hasUnsavedData,
    }: {
        override?: boolean;
        hasUnsavedData?: boolean;
    } = {}) => {
        if (!override && hasUnsavedData) {
            emitter.emit("open-dialog", {
                type: "warning",
                description: "Du har osparad data. Vill du stänga utan att spara?",
                buttons: [
                    {
                        title: "Fortsätt redigera",
                        type: "secondary",
                    },
                    {
                        title: "Ja, stäng utan att spara",
                        type: "primary",
                        action: () => {
                            store.dispatch("tabs/closeTab", tabId.value);
                        },
                    },
                ],
            });

            return;
        }

        store.dispatch("tabs/closeTab", tabId.value);
    };

    const initForm = async () => {
        if (!parameters?.value?.materialgruppId) {
            return null;
        }

        const foundMaterialgrupp = await store.dispatch("materialgrupp/getMaterialgruppById", {
            id: parameters.value.materialgruppId,
        });

        const formData = cloneDeep(foundMaterialgrupp);

        return {
            ...formData,
            material: addMinimumRows(
                formData.material?.map((item: any) => ({
                    id: item.material_display ? convertMaterialData(item.material_display) : item.material,
                    antal: item.antal,
                    materialmaterialgrupp: item.materialmaterialgrupp,
                }))
            ),
        };
    };

    const onSubmit = async (submittedMaterialGrupp: unknown) => {
        const { material = [], ...filteredSubmitted } = submittedMaterialGrupp as {
            gruppnummer: string;
            id: number;
            material: {
                id: string | number;
                antal: string | number;
                materialmaterialgrupp?: number;
            }[];
            namn: string;
            streckkod?: string;
        };

        const convertedSubmittedGrupp = {
            ...filteredSubmitted,
            materialmaterialgrupp: material
                .filter((singleMaterial) => singleMaterial && (singleMaterial.id ?? singleMaterial.antal))
                .map((singleMaterial) => ({
                    id: singleMaterial.materialmaterialgrupp,
                    material: Number(singleMaterial.id),
                    antal: Number(singleMaterial.antal),
                })),
        };

        try {
            if (isEditing.value) {
                await store.dispatch("materialgrupp/updateMaterialgrupp", {
                    materialgrupp: convertedSubmittedGrupp,
                });
            } else {
                await store.dispatch("materialgrupp/createMaterialgrupp", {
                    materialgrupp: convertedSubmittedGrupp,
                });
            }

            closeTab({ override: true });
        } catch (thrownError) {
            const error = thrownError as AxiosError;

            if (error?.response?.data) {
                errors.value = error.response.data;

                return;
            }

            flashWarning(isEditing.value ? "Kunde inte redigera materialgruppen" : "Kunde inte skapa materialgruppen");
        }
    };

    const onCancel = async ({ hasUnsavedData }: { hasUnsavedData: boolean }) => {
        closeTab({ hasUnsavedData });
    };

    const setTabName = () => {
        store.commit("tabs/setTabName", {
            id: tabId.value,
            name: isEditing.value ? "Redigera materialgrupp" : "Skapa materialgrupp",
        });
    };
    onBeforeMount(async () => {
        await store.dispatch("leverantor/addSubscriber");
        await store.dispatch("material/addSubscriber");
        await store.dispatch("materialgrupp/addSubscriber");

        setTabName();
    });

    onBeforeUnmount(async () => {
        await store.dispatch("leverantor/removeSubscriber");
        await store.dispatch("material/removeSubscriber");
        await store.dispatch("materialgrupp/removeSubscriber");
    });

    watch(materialgrupp, () => {
        setTabName();
    });
</script>

<template>
    <KForm
        ref="formRef"
        id="materialgrupp-form"
        class="material-grupp-form"
        :initial-values-getter="initForm"
        :submit-content="submitLabel"
        cancel-content="Avbryt"
        :disableSubmit="!hasUnsavedData"
        :errors="errors"
        columns="4"
        @submit="onSubmit"
        @cancel="onCancel"
    >
        <template #header>
            <h2>{{ heading }}</h2>
        </template>
        <template #default>
            <div class="form-content">
                <KText name="namn" type="text" label="Namn" validation="required" class="span-two" />
                <KGroup name="group-info" class="form-section">
                    <KText name="gruppnummer" type="text" label="Gruppnummer" validation="required" />
                    <KText name="streckkod" type="text" label="Streckkod" />
                </KGroup>
                <KGroup name="material-grupp" label="Gruppen innehåller:" class="span-all">
                    <MaterialRepeater>
                        <template #otherfields="{ index, model }">
                            <KNumber name="antal" label="Antal" :value="model?.[index]?.antal" />
                        </template>
                    </MaterialRepeater>
                </KGroup>
            </div>
        </template>
    </KForm>
</template>

<style lang="scss" scoped></style>
